import { Box, Button, Card, Stack, styled, Switch, SxProps, Typography } from '@mui/material';
import { periods } from 'entities/mine-package/model';
import { COLOR_ACCENT, COLOR_BG_SECONDARY, COLOR_BLACK, COLOR_PRIMARY, COLOR_SECONDARY } from 'shared';
import { NumberFormat } from 'shared/ui/number-format';
import { useCalculator, UseCalculatorProps } from 'widgets/buy-package/lib/use-calculator';

import { X2 } from './images';

const switchSize = {
  width: 36.5,
  height: 19,
};

const StyledSwitch = styled(Switch)({
  ...switchSize,
  '& .MuiSwitch-switchBase': {
    height: switchSize.height,
    width: switchSize.height,
    '&.Mui-checked': {
      '.MuiSwitch-thumb': {
        transform: `translateX(calc(${switchSize.width}px - 20px))`,
      },
    },
    '.MuiSwitch-input': { ...switchSize },
  },
  '& .MuiSwitch-track': { ...switchSize },
  '& .MuiSwitch-thumb': { height: 16, width: 16 },
});

interface CalculatedOutputProps {
  profitUsd: string;
  profitCrat: string;
  profitCit: string;
}

const CalculatedOutput = ({ profitUsd, profitCrat, profitCit }: CalculatedOutputProps) => {
  return (
    <Card sx={{ width: '100%', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NumberFormat prefix="$" decimals={2} withComas variant="h4" className="accent" mb={1}>
        {profitUsd}
      </NumberFormat>

      <Typography>
        <NumberFormat decimals={2} symbol="CRAT" withComas variant="body2" className="primary">
          {profitCrat}
        </NumberFormat>{' '}
        <Typography component="span" className="secondary">
          |
        </Typography>{' '}
        <NumberFormat decimals={2} symbol="CIT" withComas variant="body2" className="primary">
          {profitCit}
        </NumberFormat>
      </Typography>
    </Card>
  );
};

interface StyledPeriodButtonProps {
  $active?: boolean;
}

const StyledPeriodButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$active',
})<StyledPeriodButtonProps>(({ $active }) => {
  const btnStateStyles = $active ? { backgroundColor: COLOR_ACCENT, '&:hover': { backgroundColor: COLOR_ACCENT } } : {};

  return {
    flex: 1,
    padding: 0,
    minHeight: 34,
    height: 34,
    borderRadius: 17,
    color: COLOR_SECONDARY,
    backgroundColor: COLOR_BG_SECONDARY,
    '&:focus': { outline: 'none' },
    ...btnStateStyles,
  };
});

export const EarnCalculator = ({ sx, tap2minePackage }: { sx?: SxProps } & UseCalculatorProps) => {
  const { setSelectedPeriod, setUseX2, useX2, selectedPeriod, profitUsd, profitCrat, profitCit } = useCalculator({
    tap2minePackage,
  });

  return (
    <Stack width="100%" alignItems="center" sx={sx}>
      <Typography variant="body1" className="secondary" mb={2}>
        Period
      </Typography>
      <Stack direction="row" gap={1} mb={2} width="100%">
        {periods.map((period, idx) => (
          <StyledPeriodButton
            key={period}
            variant="contained"
            onClick={() => setSelectedPeriod(idx)}
            $active={idx === selectedPeriod}
          >
            <Typography variant="body2" color={idx === selectedPeriod ? COLOR_BLACK : COLOR_PRIMARY}>
              {period}
            </Typography>
          </StyledPeriodButton>
        ))}
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        bgcolor={COLOR_BG_SECONDARY}
        borderRadius={3}
        p={2.4}
        pl={2}
        mb={2}
      >
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="body2" className="primary">
            Power tap mode
          </Typography>
          <Box component="img" alt="x2" src={X2} height={20} />
        </Stack>

        <StyledSwitch value={useX2} onChange={(e) => setUseX2(e.target.checked)} />
      </Stack>

      <Typography variant="body1" className="secondary" mb={1}>
        Earn:
      </Typography>

      <CalculatedOutput profitUsd={profitUsd} profitCrat={profitCrat} profitCit={profitCit} />
    </Stack>
  );
};
